import type { Mixpanel as MixpanelNS } from "@iyk/tracking"

import * as React from "react"

import { mixpanel } from "@iyk/tracking"
import { Brand } from "effect"
import { monitoring } from "../monitoring/SentryMonitoring.tsx"
import { Analytics, AnalyticsContext } from "./Analytics.ts"

const withErrorHandling = <T extends unknown[], R>(fn: (...args: T) => R) => {
  return (...args: T): R | undefined => {
    try {
      return fn(...args)
    } catch (error) {
      monitoring.captureException(error as Error)
      return undefined
    }
  }
}

const analytics: Analytics = {
  identify: withErrorHandling((user) => {
    mixpanel.identify(user.id)
    mixpanel.people.set({
      $email: user.email,
      $name: user.name,
      $createdAt: user.createdAt,
      authMethod: user.authMethod
    })
  }),
  reset: withErrorHandling(() => {
    mixpanel.reset()
  }),
  track: withErrorHandling((event) => {
    const { _tag, ...rest } = event
    mixpanel.track(Analytics.EventNames[_tag], rest)
  }),
  track_links: withErrorHandling((query, event) => {
    const { _tag, ...rest } = event
    mixpanel.track_links(query, Analytics.EventNames[_tag], rest)
  })
}

const MixpanelAnalyticsProvider = ({ user, children }: MixpanelAnalytics.Props) => {
  React.useEffect(() => {
    if (user) {
      analytics.identify(user)
    }
  }, [user])

  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>
}

export const MixpanelAnalytics = {
  Provider: MixpanelAnalyticsProvider
}

export namespace MixpanelAnalytics {
  export type Props = Partial<MixpanelNS.Config> & {
    token: Mixpanel.Token
    user?: Analytics.User
    children: React.ReactNode
  }
}

export namespace Mixpanel {
  const Tag = "Mixpanel" as const
  type Tag = typeof Tag

  export type Token = Brand.Branded<string, `${Tag}/Token`>
  export const Token = Brand.nominal<Token>()

  export type Config = {
    token: Token
  }
}
